"use client";

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Floor } from "@/types/floor";
import { fetchLatestFloorplan, convertSpacesToFloors } from "@/services/floorplanService";
import { useAuth } from "@/contexts/AuthContext";
import { usePathname } from "next/navigation";
import { api } from "@/lib/api";

// 1️⃣ Context types
interface SelectedContext {
    floorId: string;
    area: string | null;
    zone: string | null;
}

interface FloorSelectionContextType {
    selectedContext: SelectedContext;
    setSelectedContext: (context: SelectedContext) => void;
    floorplan: Floor[];
    selectedFloor: Floor | undefined;
    isLoadingFloors: boolean;
    refreshFloorplan: () => Promise<void>;
}

// 2️⃣ Context
const FloorSelectionContext = createContext<FloorSelectionContextType | undefined>(undefined);

// 3️⃣ FloorSelectionProvider
interface FloorSelectionProviderProps {
    children: ReactNode;
}

export const FloorSelectionProvider: React.FC<FloorSelectionProviderProps> = ({ children }) => {
    const [selectedContext, setSelectedContextState] = useState<SelectedContext>({
        floorId: "",
        area: null,
        zone: null,
    });
    const [floorplan, setFloorplan] = useState<Floor[]>([]);
    const [isLoadingFloors, setIsLoadingFloors] = useState(true);
    const { user, loading: authLoading } = useAuth();
    const pathname = usePathname();

    // Only fetch floorplan data if user is authenticated and not on auth pages
    const isAuthPage = pathname === "/login" || pathname === "/forgot-password" || pathname === "/register" || pathname.startsWith("/login");

    // Load saved floor selection from localStorage
    const loadSavedFloorSelection = (): SelectedContext | null => {
        try {
            const saved = localStorage.getItem('selectedFloorContext');

            if (saved) {
                console.log(JSON.parse(saved), "fffffffffffffff");
                return JSON.parse(saved);
            }
        } catch (error) {
            console.warn('Failed to load saved floor selection:', error);
        }
        return null;
    };

    // Save floor selection to localStorage
    const saveFloorSelection = (context: SelectedContext) => {
        try {
            localStorage.setItem('selectedFloorContext', JSON.stringify(context));
        } catch (error) {
            console.warn('Failed to save floor selection:', error);
        }
    };

    // Wrapper function to update context and persist to localStorage
    const setSelectedContext = (context: SelectedContext) => {
        setSelectedContextState(context);
        saveFloorSelection(context);
    };

    const loadFloorplan = async () => {
        try {
            setIsLoadingFloors(true);
            let floors: Floor[] = [];
            const response = await api.get("/api/spaces");
            const data = response.data;
            if (data?.success && Array.isArray(data.spaces)) {
                floors = convertSpacesToFloors(data.spaces);
                setFloorplan(floors);
            }


            // If no floors from localStorage, try database
            if (floors.length === 0) {
                floors = await fetchLatestFloorplan();
                setFloorplan(floors);
            } else {
                setFloorplan(floors);
            }

            // Try to restore saved floor selection
            if (floors.length > 0) {
                const firstUsableFloor = floors.find(f => Array.isArray(f.areas) && f.areas.length > 0) || floors[0];
                const savedSelection = loadSavedFloorSelection();
                if (savedSelection) {
                    // Empty floorId = "All floors" (no spatial filter)
                    if (!savedSelection.floorId) {
                        setSelectedContextState({
                            floorId: "",
                            area: null,
                            zone: null,
                        });
                        saveFloorSelection({
                            floorId: "",
                            area: null,
                            zone: null,
                        });
                    } else {
                        const savedFloorExists = floors.some((f) => f.id === savedSelection.floorId);
                        if (savedFloorExists) {
                            setSelectedContextState(savedSelection);
                            saveFloorSelection(savedSelection);
                        } else {
                            const defaultContext = { floorId: firstUsableFloor.id, area: null, zone: null };
                            setSelectedContextState(defaultContext);
                            saveFloorSelection(defaultContext);
                        }
                    }
                } else {
                    const defaultContext = { floorId: firstUsableFloor.id, area: null, zone: null };
                    setSelectedContextState(defaultContext);
                    saveFloorSelection(defaultContext);
                }
            }
        } catch (error) {
            console.error('Error loading floorplan:', error);
            setFloorplan([]);
        } finally {
            setIsLoadingFloors(false);
        }
    };

    // Fetch real floorplan data on component mount (only if authenticated)
    useEffect(() => {
        // Don't fetch if on auth pages or if auth is still loading
        if (isAuthPage || authLoading) {
            setIsLoadingFloors(false);
            return;
        }

        // Only fetch if user is authenticated
        if (user) {
            loadFloorplan();
        } else {
            setIsLoadingFloors(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, isAuthPage]);

    // Listen for storage events to refresh when floorplan is updated
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'floorplanUpdated') {
                refreshFloorplan();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const refreshFloorplan = async () => {
        // Don't refresh if not authenticated or on auth pages
        if (!user || isAuthPage) {
            return;
        }

        // Use same logic as loadFloorplan so we get fresh data from /api/spaces
        await loadFloorplan();
        return;
    };

    const selectedFloor = floorplan.find(f => f.id === selectedContext.floorId);

    return (
        <FloorSelectionContext.Provider
            value={{
                selectedContext,
                setSelectedContext,
                floorplan,
                selectedFloor,
                isLoadingFloors,
                refreshFloorplan,
            }}
        >
            {children}
        </FloorSelectionContext.Provider>
    );
};

// 4️⃣ Custom hook
export const useFloorSelection = (): FloorSelectionContextType => {
    const context = useContext(FloorSelectionContext);
    if (!context) {
        throw new Error("useFloorSelection must be used within a FloorSelectionProvider");
    }
    return context;
};